import { areArraysEqual, isDefined, isNotDefined } from "@/utils/assertions";
import clsx from "clsx";
import { startOfDay } from "date-fns/startOfDay";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PickerState } from "../SgPicker/types";
import type { Size } from "../layout";
import {
  asIsoDate,
  fromIsoToDate,
  fromTimestamp,
  toDateString,
  toTimestamp,
} from "./date-utilities";
import type { DateSelectedEvent, SgDatepickerHTMLElement } from "./types";

export type DatePickerType = "multiple" | "single";

export interface SgDatePickerProps<Type extends DatePickerType = "multiple"> {
  availableDates?: string;
  appendTo?: string;
  acceptEmptyValue?: boolean;
  className?: string;
  closeAfterSelect?: boolean;
  closeWhenClickOutside?: boolean;
  datePresets?: Record<string, Date>;
  e2e?: string;
  format?: string;
  displayFormat?: string;
  placeholder?: string;
  id?: string;
  isDisabled?: boolean;
  isReadonly?: boolean;
  label?: string;
  disablePresets?: string;
  selectionType: Type;
  maxValue?: string;
  onChange: Type extends "multiple"
    ? (values: readonly string[]) => void
    : (value?: string) => void;
  showClearButton?: boolean;
  size?: Size;
  validationMessage?: string;
  validationState?: PickerState;
  values?: (Date | string)[];
  // TODO: rename yearControllersDisabled
  yearControllers?: boolean;
  locale?: string;
}

/**
 * Wrap of the <sg-date-picker> component from SG Bootstrap 5.
 *
 * @example
 * <DatePicker size="lg" onChange={doAction}></DatePicker>
 */
export function SgDatePicker<Type extends DatePickerType>({
  availableDates,
  appendTo = "body",
  acceptEmptyValue = false,
  className,
  closeAfterSelect = true,
  closeWhenClickOutside = true,
  datePresets,
  e2e,
  format = "dd MMM yyyy",
  displayFormat = "dd MMM yyyy",
  placeholder,
  id,
  isDisabled = false,
  isReadonly = false,
  label,
  disablePresets,
  onChange,
  selectionType,
  showClearButton = true,
  size = "md",
  validationMessage = "",
  validationState,
  values = [],
  yearControllers = false,
  locale = "en-GB",
}: Readonly<SgDatePickerProps<Type>>) {
  const innerRef = useRef<SgDatepickerHTMLElement>(null);
  const [isReady, setIsReady] = useState(false);

  const definedDateValues = useMemo(
    () => values.filter(isDefined).map((value) => startOfDay(new Date(value))),
    [values]
  );

  const definedDateTimestamps = useMemo(
    () =>
      definedDateValues
        ? definedDateValues.map((date) => toTimestamp(date.toISOString())).filter(isDefined)
        : undefined,
    [definedDateValues]
  );

  const formattedDateValues = useMemo(
    () => definedDateValues.map((value) => toDateString(value, format)),
    [definedDateValues, format]
  );

  const onChangeDateValues = useCallback(
    (newValues: readonly number[]) => {
      const timestampsAsDates = newValues
        .map((newValue) => fromIsoToDate(fromTimestamp(newValue)))
        .filter(isDefined)
        .map((date) => date.toISOString());
      if (selectionType === "multiple") {
        (onChange as (values: readonly string[]) => void)(timestampsAsDates);
      } else {
        const singleOnChange = onChange as (value?: string) => void;
        if (newValues.length === 0) {
          singleOnChange(undefined);
        } else {
          singleOnChange(timestampsAsDates[timestampsAsDates.length - 1]);
        }
      }
    },
    [selectionType, onChange]
  );

  const onReady = (): void => {
    setIsReady(true);
  };

  const onDateSelected = useCallback(
    (event: DateSelectedEvent) => {
      const timestamp = event.detail.timestamp;

      if (isNotDefined(definedDateTimestamps) || isNotDefined(timestamp)) {
        return;
      }
      if (!definedDateTimestamps?.includes(timestamp)) {
        onChangeDateValues([...definedDateTimestamps, timestamp]);
      }
    },
    [definedDateTimestamps, onChangeDateValues]
  );

  const onCleared = useCallback(() => {
    onChangeDateValues([]);
  }, [onChangeDateValues]);

  const synchronizeValidityState = useCallback(() => {
    if (!innerRef.current) {
      return;
    }
    innerRef.current.setValidationState(validationState ?? null, "");
  }, [validationState]);

  useEffect(() => {
    (async () => {
      if (!isReady || !innerRef.current) {
        return;
      }
      const currentSelection = await innerRef.current.getSelection();

      if (isNotDefined(definedDateTimestamps) || definedDateTimestamps.length === 0) {
        innerRef.current.resetSelection();
      } else if (!areArraysEqual(currentSelection, definedDateTimestamps)) {
        innerRef.current.setSelection(definedDateTimestamps);

        synchronizeValidityState();
      }
    })();
  }, [isReady, definedDateTimestamps, synchronizeValidityState]);

  useEffect(() => {
    if (!innerRef.current) {
      return;
    }
    innerRef.current.customPresets = isDefined(datePresets)
      ? Object.entries(datePresets).reduce(
          // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
          (prev, [lable, date]) => ({ ...prev, [lable]: asIsoDate(date) }),
          {}
        )
      : undefined;
  }, [datePresets]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const ref = innerRef.current;
    if (ref) {
      ref.addEventListener("ready", onReady);
      ref.addEventListener("dateSelected", onDateSelected as EventListener);
      ref.addEventListener("inputCleared", onCleared as EventListener);
      ref.addEventListener("calendarClose", synchronizeValidityState as EventListener);
    }
    return () => {
      if (!ref) {
        return;
      }
      ref.removeEventListener("ready", onReady);
      ref.removeEventListener("dateSelected", onDateSelected as EventListener);
      ref.removeEventListener("inputCleared", onCleared as EventListener);
      ref.removeEventListener("calendarClose", synchronizeValidityState as EventListener);
    };
  }, [onCleared, onDateSelected, synchronizeValidityState]);

  return (
    <sg-date-picker
      append-to={appendTo}
      accept-empty-value={acceptEmptyValue}
      classes={clsx(className)}
      close-after-select={closeAfterSelect}
      close-when-click-outside={closeWhenClickOutside}
      data-e2e={`${e2e}-date-picker`}
      disable-presets={disablePresets}
      disabled={isDisabled}
      display-format={displayFormat}
      format={format}
      formatted-default-value={formattedDateValues.length > 0 ? formattedDateValues[0] : undefined}
      placeholder={placeholder}
      id={id}
      input-id={`${id}-date-picker`}
      item-selection={selectionType === "multiple" ? "multiple" : "single"}
      labelled-by={label}
      readonly={isReadonly}
      ref={innerRef}
      show-clear-button={showClearButton}
      size={size}
      validation-state={validationState}
      validation-message={validationMessage}
      year-controllers={yearControllers}
      available-dates={availableDates}
      locale={locale}
    />
  );
}
